import { translateInstitutionListDimensionsForLocale, useLocale, useQueryParamState } from '@modules';
import { FsTypeBase, InstitutionWithImage } from '@types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getInstitutionsForPeriodByType } from '../networking';

type UseInstitutionDropDownControlProps = {
  type?: FsTypeBase<string>;
  period?: FsTypeBase<string>;
  paramKey: string;
};

export const useInstitutionDropDownControl = ({ type, period, paramKey }: UseInstitutionDropDownControlProps) => {
  const { institutions, institution, setInstitutionId, fetchInstitutions } = useInstitutions(paramKey, type, period);

  const setHandler = (e: string) => {
    // matching of institution is done differently in FS tool: CC.NAME
    const selectedInstitution = institutions.find((i) => `${i.country.countryCode}.${i.name}` === e)?.id;
    setInstitutionId(paramKey, selectedInstitution ? String(selectedInstitution) : '');
  };

  return {
    institutions,
    institution,
    setInstitutionId,
    institutionSetHandler: setHandler,
    isLoading: institutions.length === 0,
    getInstitutions: fetchInstitutions,
  };
};

function useInstitutions(paramKey: string, type?: FsTypeBase<string>, period?: FsTypeBase<string>) {
  const [institutions, setInstitutions] = useState<InstitutionWithImage[]>([]);
  const [institutionId, setInstitutionId] = useQueryParamState(paramKey);

  const fetchInstitutions = useFetchInstitutions(setInstitutions, type, period);

  const institution = useMemo(() => {
    if (!institutions.length) return undefined;
    return institutions.find((i) => String(i.id) === institutionId) ?? institutions[0];
  }, [institutions, institutionId]);

  return useMemo(
    () => ({ institutions, institution, setInstitutionId, fetchInstitutions }),
    [institutions, institution, setInstitutionId, fetchInstitutions]
  );
}

function useFetchInstitutions(
  setInstitutions: (institutions: InstitutionWithImage[]) => void,
  type?: FsTypeBase<string>,
  period?: FsTypeBase<string>
) {
  const locale = useLocale();

  const fetchInstitutions = useCallback(async () => {
    setInstitutions([]);

    if (type && period) {
      const res = await getInstitutionsForPeriodByType(type.text, period?.id);
      if (res.list) {
        const translatedInstitutions = translateInstitutionListDimensionsForLocale(res.list, res.translations, locale);
        setInstitutions(translatedInstitutions);
      }
    }
  }, [type, period, locale]);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      if (mounted) {
        await fetchInstitutions();
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [fetchInstitutions]);

  return fetchInstitutions;
}
